/** XeAgents.ai**/
import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Upload, Plus, Trash2, ChevronDown, ChevronUp, Clock, Pencil } from "lucide-react";
import { AuthStorage } from "@/lib/auth-storage";
import { useVoiceEngine, visibleAgents } from "@/lib/voice-engine";

interface Agent {
  id: string;
  name: string;
  personality: string;
  type: 'incoming' | 'flow';
  isActive?: boolean;
  voiceProvider?: string;
}

interface Campaign {
  id: string;
  name: string;
  type: string;
  goal?: string;
  script?: string;
  agentId: string;
  voiceId?: string;
  scheduleEnabled?: boolean;
  scheduleTimeStart?: string;
  scheduleTimeEnd?: string;
  scheduleDays?: string[];
  scheduleTimezone?: string;
}

interface EditCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign | null;
}

export function EditCampaignDialog({ open, onOpenChange, campaign }: EditCampaignDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [showContactsSection, setShowContactsSection] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    type: "Lead Qualification",
    goal: "",
    script: "",
    agentId: "",
    scheduleEnabled: false,
    scheduleTimeStart: "09:00",
    scheduleTimeEnd: "17:00",
    scheduleDays: [] as string[],
    scheduleTimezone: "America/New_York",
  });
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [manualContacts, setManualContacts] = useState<Array<{firstName: string; lastName: string; phone: string; email: string}>>([]);
  const [currentContact, setCurrentContact] = useState({firstName: "", lastName: "", phone: "", email: ""});

  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign.name || "",
        type: campaign.type || "Lead Qualification",
        goal: campaign.goal || "",
        script: campaign.script || "",
        agentId: campaign.agentId || "",
        scheduleEnabled: campaign.scheduleEnabled || false,
        scheduleTimeStart: campaign.scheduleTimeStart || "09:00",
        scheduleTimeEnd: campaign.scheduleTimeEnd || "17:00",
        scheduleDays: campaign.scheduleDays || [],
        scheduleTimezone: campaign.scheduleTimezone || "America/New_York",
      });
    }
  }, [campaign]);

  const { data: allAgents = [] } = useQuery<Agent[]>({
    queryKey: ["/api/agents"],
    enabled: open,
  });
  // With Nova Sonic selected by the super admin, agents that run on ElevenLabs are not offered; the agent this
  // campaign already uses stays in the list so its dropdown is never blank.
  const { novaSelected } = useVoiceEngine();
  const agents = useMemo(() => visibleAgents(allAgents, novaSelected, [campaign?.agentId]), [allAgents, novaSelected, campaign?.agentId]);

  // Filter agents to only show flow agents with compatible voice providers
  // (same filter as CreateCampaignDialog for consistency)
  const compatibleAgents = useMemo(() => {
    return agents.filter(agent => {
      const compatibleProvider = !agent.voiceProvider ||
        agent.voiceProvider === 'elevenlabs' ||
        agent.voiceProvider === 'bulbul' ||
        agent.voiceProvider === 'nova_sonic' || agent.voiceProvider === 'nova-sonic' ||
        agent.voiceProvider === 'gemini' ||
        agent.voiceProvider === 'kie-elevenlabs';
      return agent.type === 'flow' && agent.isActive !== false && compatibleProvider;
    });
  }, [agents]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!campaign) return;
      const res = await apiRequest("PATCH", `/api/campaigns/${campaign.id}`, formData);
      return res.json();
    },
    onSuccess: async () => {
      if (csvFile && campaign) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", csvFile);

        try {
          const uploadHeaders: Record<string, string> = {};
          const authHeader = AuthStorage.getAuthHeader();
          if (authHeader) {
            uploadHeaders["Authorization"] = authHeader;
          }
          
          const res = await fetch(`/api/campaigns/${campaign.id}/contacts/upload`, {
            method: "POST",
            headers: uploadHeaders,
            body: formDataUpload,
          });

          if (!res.ok) {
            throw new Error("Failed to upload CSV contacts");
          }
          
          toast({ title: t("campaigns.toast.csvUploadSuccess") });
        } catch (error) {
          toast({
            title: t("campaigns.toast.csvUploadFailed"),
            description: t("campaigns.toast.csvCheckFormat"),
            variant: "destructive",
          });
        }
      }

      // Build final contacts list, auto-including any unsaved current contact (same as CreateCampaignDialog)
      const finalContacts = [...manualContacts];
      if (currentContact.firstName && currentContact.phone) {
        finalContacts.push(currentContact);
      }

      if (finalContacts.length > 0 && campaign) {
        try {
          const contacts = finalContacts.map(c => ({
            firstName: c.firstName,
            lastName: c.lastName,
            phone: c.phone,
            email: c.email,
          }));

          const res = await apiRequest("POST", `/api/campaigns/${campaign.id}/contacts/bulk`, { contacts });

          if (!res.ok) {
            throw new Error("Failed to add manual contacts");
          }

          toast({ title: t("campaigns.toast.manualContactsSuccess") });
          setManualContacts([]);
          setCurrentContact({firstName: "", lastName: "", phone: "", email: ""});
          
          // Invalidate the contacts query to refresh the receipts tab
          queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaign.id}/contacts`] });
        } catch (error) {
          toast({
            title: t("campaigns.toast.manualContactsFailed"),
            variant: "destructive",
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      toast({ title: t("campaigns.toast.updatedSuccess") });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: t("campaigns.toast.updateFailed"),
        description: error.message || t("common.tryAgain"),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!formData.name) {
      toast({ title: t("campaigns.toast.nameRequired"), description: t("campaigns.toast.pleaseEnterCampaignName"), variant: "destructive" });
      return;
    }
    if (!formData.agentId) {
      toast({ title: t("campaigns.toast.missingRequired"), description: t("campaigns.toast.pleaseSelectAgent"), variant: "destructive" });
      return;
    }
    updateMutation.mutate();
  };

  const handleAddContact = () => {
    if (!currentContact.firstName || !currentContact.phone) {
      toast({ 
        title: t("campaigns.toast.invalidContact"), 
        description: t("campaigns.toast.firstNamePhoneRequired"),
        variant: "destructive" 
      });
      return;
    }
    setManualContacts([...manualContacts, currentContact]);
    toast({ 
      title: t("campaigns.toast.contactAdded"), 
      description: t("campaigns.toast.contactAddedDesc", { name: currentContact.firstName }) 
    });
    setCurrentContact({firstName: "", lastName: "", phone: "", email: ""});
  };

  const handleRemoveContact = (index: number) => {
    setManualContacts(manualContacts.filter((_, i) => i !== index));
  };

  const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-md">
              <Pencil className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle>{t("campaigns.edit.title")}</DialogTitle>
              <DialogDescription>{t("campaigns.edit.subtitle")}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-campaign-name">{t("campaigns.edit.name")}</Label>
            <Input
              id="edit-campaign-name"
              placeholder={t("campaigns.edit.namePlaceholder")}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              data-testid="input-edit-campaign-name"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center">
              <Label htmlFor="edit-campaign-type">{t("campaigns.edit.type")}</Label>
              <InfoTooltip content={t("campaigns.create.typeTooltip")} />
            </div>
            <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
              <SelectTrigger data-testid="select-edit-campaign-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Lead Qualification">{t("campaigns.create.typeOptions.lead")}</SelectItem>
                <SelectItem value="Feedback Collection">{t("campaigns.create.typeOptions.feedback")}</SelectItem>
                <SelectItem value="Promotional">{t("campaigns.create.typeOptions.promotional")}</SelectItem>
                <SelectItem value="Payment Reminder">{t("campaigns.create.typeOptions.payment")}</SelectItem>
                <SelectItem value="Event Promotion">{t("campaigns.create.typeOptions.event")}</SelectItem>
                <SelectItem value="Survey">{t("campaigns.create.typeOptions.survey")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-campaign-goal">{t("campaigns.edit.goalOptional")}</Label>
            <Textarea
              id="edit-campaign-goal"
              placeholder={t("campaigns.edit.goalPlaceholder")}
              value={formData.goal}
              onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
              rows={2}
              data-testid="input-edit-campaign-goal"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-agent-select">{t("campaigns.edit.agentRequired")}</Label>
            <Select value={formData.agentId} onValueChange={(value) => setFormData({ ...formData, agentId: value })}>
              <SelectTrigger data-testid="select-edit-agent">
                <SelectValue placeholder={compatibleAgents.length === 0 ? t("campaigns.create.noAgentsAvailable") : t("campaigns.edit.agentPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {compatibleAgents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name} - {agent.personality} ({t("agents.type.flow")})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {compatibleAgents.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t("campaigns.create.noAgentsAvailable")} Campaigns require agents with ElevenLabs, Nova Sonic, Gemini, or Kie.ai ElevenLabs voice provider.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-campaign-script">{t("campaigns.edit.customScriptOptional")}</Label>
            <Textarea
              id="edit-campaign-script"
              placeholder={t("campaigns.edit.scriptPlaceholder")}
              value={formData.script}
              onChange={(e) => setFormData({ ...formData, script: e.target.value })}
              rows={3}
              data-testid="input-edit-campaign-script"
            />
          </div>

          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <Label className="text-base">{t("campaigns.schedule.titleOptional")}</Label>
                <InfoTooltip content={t("campaigns.schedule.tooltip")} />
              </div>
              <Switch
                checked={formData.scheduleEnabled}
                onCheckedChange={(checked) => setFormData({ ...formData, scheduleEnabled: checked })}
                data-testid="switch-edit-schedule-enabled"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {t("campaigns.schedule.description")}
            </p>

            {formData.scheduleEnabled && (
              <div className="space-y-4 pl-7">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Label htmlFor="edit-schedule-time-start">{t("campaigns.schedule.startTime")}</Label>
                      <InfoTooltip content={t("campaigns.schedule.startTimeTooltip")} />
                    </div>
                    <Input
                      id="edit-schedule-time-start"
                      type="time"
                      value={formData.scheduleTimeStart}
                      onChange={(e) => setFormData({ ...formData, scheduleTimeStart: e.target.value })}
                      data-testid="input-edit-schedule-time-start"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Label htmlFor="edit-schedule-time-end">{t("campaigns.schedule.endTime")}</Label>
                      <InfoTooltip content={t("campaigns.schedule.endTimeTooltip")} />
                    </div>
                    <Input
                      id="edit-schedule-time-end"
                      type="time"
                      value={formData.scheduleTimeEnd}
                      onChange={(e) => setFormData({ ...formData, scheduleTimeEnd: e.target.value })}
                      data-testid="input-edit-schedule-time-end"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <Label>{t("campaigns.schedule.daysOfWeek")}</Label>
                    <InfoTooltip content={t("campaigns.schedule.daysTooltip")} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {dayKeys.map((day) => (
                      <div key={day} className="flex items-center gap-2">
                        <Checkbox
                          id={`edit-day-${day}`}
                          checked={formData.scheduleDays.includes(day)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, scheduleDays: [...formData.scheduleDays, day] });
                            } else {
                              setFormData({ ...formData, scheduleDays: formData.scheduleDays.filter(d => d !== day) });
                            }
                          }}
                          data-testid={`checkbox-edit-day-${day}`}
                        />
                        <Label htmlFor={`edit-day-${day}`} className="text-sm font-normal cursor-pointer">
                          {t(`campaigns.schedule.days.${day}`)}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <Label htmlFor="edit-schedule-timezone">{t("campaigns.schedule.timezone")}</Label>
                    <InfoTooltip content={t("campaigns.schedule.timezoneTooltip")} />
                  </div>
                  <Select 
                    value={formData.scheduleTimezone} 
                    onValueChange={(value) => setFormData({ ...formData, scheduleTimezone: value })}
                  >
                    <SelectTrigger data-testid="select-edit-schedule-timezone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/New_York">{t("campaigns.schedule.timezones.eastern")}</SelectItem>
                      <SelectItem value="America/Chicago">{t("campaigns.schedule.timezones.central")}</SelectItem>
                      <SelectItem value="America/Denver">{t("campaigns.schedule.timezones.mountain")}</SelectItem>
                      <SelectItem value="America/Los_Angeles">{t("campaigns.schedule.timezones.pacific")}</SelectItem>
                      <SelectItem value="America/Phoenix">{t("campaigns.schedule.timezones.arizona")}</SelectItem>
                      <SelectItem value="America/Anchorage">{t("campaigns.schedule.timezones.alaska")}</SelectItem>
                      <SelectItem value="Pacific/Honolulu">{t("campaigns.schedule.timezones.hawaii")}</SelectItem>
                      <SelectItem value="Europe/London">{t("campaigns.schedule.timezones.london")}</SelectItem>
                      <SelectItem value="Europe/Paris">{t("campaigns.schedule.timezones.paris")}</SelectItem>
                      <SelectItem value="Asia/Tokyo">{t("campaigns.schedule.timezones.tokyo")}</SelectItem>
                      <SelectItem value="Asia/Dubai">{t("campaigns.schedule.timezones.dubai")}</SelectItem>
                      <SelectItem value="Australia/Sydney">{t("campaigns.schedule.timezones.sydney")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowContactsSection(!showContactsSection)}
              className="w-full justify-between"
              data-testid="button-toggle-contacts-section"
            >
              <span>{showContactsSection ? t("campaigns.edit.hideContacts") : t("campaigns.edit.addContacts")}</span>
              {showContactsSection ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <p className="text-sm text-muted-foreground mt-2">
              {showContactsSection ? t("campaigns.edit.addNewContacts") : t("campaigns.edit.clickToAddContacts")}
            </p>
          </div>

          {showContactsSection && (
            <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
              <Tabs defaultValue="csv" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="csv" data-testid="tab-csv-upload">{t("campaigns.contacts.csvUpload")}</TabsTrigger>
                  <TabsTrigger value="manual" data-testid="tab-manual-entry">{t("campaigns.contacts.manualEntry")}</TabsTrigger>
                </TabsList>

                <TabsContent value="csv" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-contacts-csv">{t("campaigns.contacts.uploadCSVFile")}</Label>
                    <div className="border-2 border-dashed rounded-md p-6 text-center hover-elevate">
                      <input
                        id="edit-contacts-csv"
                        type="file"
                        accept=".csv"
                        onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                        className="hidden"
                        data-testid="input-edit-csv-file"
                      />
                      <label htmlFor="edit-contacts-csv" className="cursor-pointer">
                        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        {csvFile ? (
                          <p className="text-sm font-medium">{csvFile.name}</p>
                        ) : (
                          <>
                            <p className="text-sm font-medium">{t("campaigns.contacts.clickToUploadCSV")}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {t("campaigns.contacts.includeColumns")}
                            </p>
                          </>
                        )}
                      </label>
                    </div>
                  </div>

                  <div className="bg-muted p-3 rounded-md">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium">{t("campaigns.contacts.csvFormatExample")}</p>
                      <a 
                        href="/campaign_template.csv"
                        download="campaign_template.csv"
                        className="text-xs text-primary hover:underline"
                        data-testid="link-edit-download-sample-csv"
                      >
                        {t("campaigns.contacts.downloadSampleCSV")}
                      </a>
                    </div>
                    <code className="text-xs block bg-background p-2 rounded">
                      phone_number,language,voice_id,first_message,prompt,city,other_dyn_variable<br />
                      +12345678900,en,,,,London,<br />
                      +48517067931,pl,,,,Warsaw,
                    </code>
                  </div>
                </TabsContent>

                <TabsContent value="manual" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="edit-manual-first-name">{t("campaigns.contacts.firstNameRequired")}</Label>
                      <Input
                        id="edit-manual-first-name"
                        placeholder={t("campaigns.contacts.firstNamePlaceholder")}
                        value={currentContact.firstName}
                        onChange={(e) => setCurrentContact({ ...currentContact, firstName: e.target.value })}
                        data-testid="input-edit-manual-first-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-manual-last-name">{t("campaigns.contacts.lastName")}</Label>
                      <Input
                        id="edit-manual-last-name"
                        placeholder={t("campaigns.contacts.lastNamePlaceholder")}
                        value={currentContact.lastName}
                        onChange={(e) => setCurrentContact({ ...currentContact, lastName: e.target.value })}
                        data-testid="input-edit-manual-last-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-manual-phone">{t("campaigns.contacts.phoneRequired")}</Label>
                      <Input
                        id="edit-manual-phone"
                        placeholder={t("campaigns.contacts.phonePlaceholder")}
                        value={currentContact.phone}
                        onChange={(e) => setCurrentContact({ ...currentContact, phone: e.target.value })}
                        data-testid="input-edit-manual-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-manual-email">{t("campaigns.contacts.email")}</Label>
                      <Input
                        id="edit-manual-email"
                        type="email"
                        placeholder={t("campaigns.contacts.emailPlaceholder")}
                        value={currentContact.email}
                        onChange={(e) => setCurrentContact({ ...currentContact, email: e.target.value })}
                        data-testid="input-edit-manual-email"
                      />
                    </div>
                  </div>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={handleAddContact}
                    className="w-full"
                    data-testid="button-edit-add-contact"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t("campaigns.contacts.addContact")}
                  </Button>

                  {manualContacts.length > 0 && (
                    <div className="space-y-2">
                      <Label>{t("campaigns.contacts.addedContactsCount", { count: manualContacts.length })}</Label>
                      <div className="border rounded-md max-h-32 overflow-y-auto">
                        {manualContacts.map((contact, index) => (
                          <div key={index} className="flex items-center justify-between p-2 hover:bg-muted/50">
                            <div className="flex-1">
                              <span className="font-medium text-sm">{contact.firstName} {contact.lastName || ""}</span>
                              <span className="text-sm text-muted-foreground ml-2">{contact.phone}</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveContact(index)}
                              data-testid={`button-edit-remove-contact-${index}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-edit">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending} data-testid="button-save-campaign-edit">
            {updateMutation.isPending ? t("campaigns.actions.saving") : t("campaigns.actions.saveChanges")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
